import React, { useCallback } from "react";
import { useToast } from "./useToast";
import { cloneDeep, unset } from "lodash";
import { ButtonProps } from "@chakra-ui/button";

export interface CopyFormProps<T> {
  key: string;
  copySuccessText?: string;
  pasteSuccessText?: string;
  errorText?: string;
  setValues: (values: React.SetStateAction<T>) => void;
  excludedFields?: string[];
  copyButtonProps?: ButtonProps;
}

export function useCopyForm<T>({
  key,
  copySuccessText,
  pasteSuccessText,
  errorText,
  setValues,
  excludedFields,
  copyButtonProps,
}: CopyFormProps<T>) {
  const { showErrorToast, showSuccessToast } = useToast();

  const copyForm = useCallback(
    (values: T) => {
      const cloned = cloneDeep(values);
      let result: { [key: string]: any } = {};
      excludedFields?.forEach((field) => {
        unset(cloned, field);
      });
      result[key] = cloned;
      navigator.clipboard.writeText(JSON.stringify(result, null, 2));
      showSuccessToast(copySuccessText ?? "Copied to clipboard!");
    },
    [navigator.clipboard]
  );

  const pasteForm = useCallback(async () => {
    try {
      let clipboardResult = await navigator.clipboard.readText();
      const resultObject = JSON.parse(clipboardResult);
      if (!resultObject.hasOwnProperty(key)) {
        return showErrorToast(errorText ?? "Not valid values");
      }
      setValues(resultObject[key]);
      showSuccessToast(pasteSuccessText ?? "Paste form success!");
    } catch (e) {
      showErrorToast(errorText ?? "Not valid values");
    }
  }, [navigator.clipboard]);

  return { copyForm, pasteForm };
}
